<template>
  <div class="flex gap-4" :data-test="`link-container-${element.name}`">
    <div class="flex-grow">
      <DetailsListEntry v-if="linkValue" class="ma-0" :title="element.label" :content="readModeLinkValue" />
      <slot v-else />
    </div>
    <div
      v-if="linkedProtocol"
      class="flex flex-grow-0 h-10 w-10 bg-primary justify-center items-center self-end rounded cursor-pointer"
      @click="linkedProtocolModalOpen = true"
    >
      <Icon name="action/details" color="white" />
    </div>
    <div
      v-if="edit"
      class="flex flex-grow-0 h-10 w-10 bg-primary justify-center items-center self-end rounded cursor-pointer"
      @click="changeLinkModalOpen = true"
    >
      <Icon data-test="icon-link" name="action/link" color="white" />
    </div>

    <Modal
      v-if="linkedProtocol && linkedProtocolModalOpen"
      data-test-id="linked-protocol"
      is-open
      :title="$tc('protocol.linking.linked_protocol', undefined, { field: element.label })"
      :max-width="800"
      view-only
      @cancel="linkedProtocolModalOpen = false"
    >
      <ProtocolDetails :protocol="linkedProtocol" one-column-layout />
    </Modal>
    <ProtocolLinkDialog
      v-if="edit && changeLinkModalOpen"
      :element="element"
      :link="link"
      @link="linkValue = $event"
      @cancel="changeLinkModalOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { Element, isProtocolLink, ProtocolLink } from '@anschuetz-elog/common';
import { computed, defineComponent, PropType, ref, toRef } from 'vue';

import DetailsListEntry from '#/components/DetailsListEntry.vue';
import { getProtocolLinkValue } from '#/components/form/utils';
import Icon from '#/components/Icon.vue';
import Modal from '#/components/Modal.vue';
import ProtocolDetails from '#/components/protocol/ProtocolDetails.vue';
import useGet from '#/compositions/useGet';

import ProtocolLinkDialog from './ProtocolLinkDialog.vue';
import { ProtocolLinkConfigProp } from './utils';

export default defineComponent({
  name: 'ProtocolLinkContainer',

  components: {
    DetailsListEntry,
    Icon,
    Modal,
    ProtocolDetails,
    ProtocolLinkDialog,
  },

  props: {
    value: {
      type: Object as PropType<Record<string, unknown>>,
      default: () => ({}),
    },

    element: {
      type: Object as PropType<Element>,
      required: true,
    },

    edit: {
      type: Boolean,
    },

    link: {
      type: Object as PropType<ProtocolLinkConfigProp>,
      required: true,
    },
  },

  emits: {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    input: (_value: Record<string, unknown>) => true,
  },

  setup(props, { emit }) {
    const value = toRef(props, 'value');
    const element = toRef(props, 'element');
    const link = toRef(props, 'link');

    const linkedProtocolModalOpen = ref(false);
    const changeLinkModalOpen = ref(false);

    const linkValue = computed<ProtocolLink | undefined>({
      get() {
        const model = value.value[element.value.name];
        if (!isProtocolLink(model)) {
          return undefined;
        }
        return model;
      },
      set(newLinkValue) {
        emit('input', { ...value.value, [element.value.name]: newLinkValue });
      },
    });

    const readModeLinkValue = computed(() => {
      return getProtocolLinkValue(linkValue.value, false);
    });

    const protocolId = computed(() =>
      link.value !== undefined && linkValue.value !== undefined ? linkValue.value.protocol._id : undefined,
    );
    const { data: linkedProtocol } = useGet('protocol', protocolId);

    return {
      linkValue,
      readModeLinkValue,
      linkedProtocolModalOpen,
      changeLinkModalOpen,
      protocolId,
      linkedProtocol,
    };
  },
});
</script>
